import type React from "react";
import { createContext, useContext, useEffect, useState } from "react";
import { Link } from "react-router";

import ChartDot from "../molecules/ChartDot";
import { InfoIconButton } from "../molecules/InfoIconButton";
import Line, { type Position } from "../molecules/Line";
import { type DocumentSummary } from "~/api/types";
import { useTranslation } from "react-i18next";
import DocumentIcon from "../molecules/DocumentIcon";
import { StatusBadge } from "../molecules/StatusBadge";
import { formatDate, isPastDate } from "~/helpers/date";
import { getCategoryTitle } from "~/helpers/category";
import { getTierTitle } from "~/helpers/tier";
import cn from "classnames";
import { GripVertical } from "lucide-react";
import { Button } from "~/atoms/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";

interface ChartReorderContextValue {
  reorderKey: number;
  notifyReorderStart: () => void;
}

export const ChartReorderContext = createContext<ChartReorderContextValue>({
  reorderKey: 0,
  notifyReorderStart: () => undefined,
});

interface ChartLinkedDocumentsContextValue {
  documentsById: Map<number, DocumentSummary>;
  tierOrderById: Map<number, number>;
}

export const ChartLinkedDocumentsContext =
  createContext<ChartLinkedDocumentsContextValue>({
    documentsById: new Map(),
    tierOrderById: new Map(),
  });

const DocumentLinkList = ({ documents }: { documents: DocumentSummary[] }) => (
  <ul className="list-disc pl-4">
    {documents.map((document) => (
      <li key={document.id}>
        <Button
          variant="link"
          asChild
          className="h-auto p-0 text-xs break-all whitespace-normal"
        >
          <Link
            to={`/documents/${document.id}?releaseId=${(document.latestPublishedRelease ?? document.latestRelease).id}`}
          >
            {document.latestRelease.artifact.title}
          </Link>
        </Button>
      </li>
    ))}
  </ul>
);

const LinkedDocumentsTooltip = ({
  documents,
}: {
  documents: DocumentSummary[];
}) => {
  const { t } = useTranslation();

  if (documents.length === 0) {
    return null;
  }

  const groups = new Map<
    string,
    { label: string; documents: DocumentSummary[] }
  >();
  for (const document of documents) {
    const key = `${document.tier?.id ?? "none"}-${document.category?.id ?? "none"}`;
    const label = [
      getTierTitle(document.tier?.title, t),
      getCategoryTitle(document.category?.title, t),
    ]
      .filter(Boolean)
      .join(" · ");
    const group = groups.get(key) ?? { label, documents: [] };
    group.documents.push(document);
    groups.set(key, group);
  }

  return (
    <TooltipContent className="flex flex-col gap-2 p-2">
      <div className="text-xs font-medium">
        {t(($) => $.chart.otherLinkedDocuments)}
      </div>
      {Array.from(groups.entries()).map(([key, group]) => (
        <div key={key} className="flex flex-col gap-1">
          <div className="text-muted-foreground text-xs font-semibold">
            {group.label}
          </div>
          <DocumentLinkList documents={group.documents} />
        </div>
      ))}
    </TooltipContent>
  );
};

const LinkedDocumentsDot = ({
  position,
  active,
  documents,
}: {
  position: "top" | "bottom";
  active: boolean;
  documents: DocumentSummary[];
}) => (
  <div
    className={cn(
      "absolute left-1/2 z-10 -translate-x-1/2",
      position === "top" ? "-top-1.5" : "-bottom-3",
    )}
  >
    <Tooltip>
      <TooltipTrigger asChild>
        <ChartDot active={active} count={documents.length} />
      </TooltipTrigger>
      <LinkedDocumentsTooltip documents={documents} />
    </Tooltip>
  </div>
);

interface Props {
  document: DocumentSummary;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  hoveredId?: number;
  setHoveredId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setSelectedId: (id: number) => void;
  isDragging?: boolean;
  handleRef?: (element: Element | null) => void;
}

interface PositionWithId extends Position {
  toId: number;
}

interface DocumentLinesProps {
  document: DocumentSummary;
  documentRefsMap: React.RefObject<Map<number, HTMLDivElement>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  hoveredId?: number;
  documents: DocumentSummary[];
}

export const DocumentLines = ({
  document,
  documentRefsMap,
  containerRef,
  hoveredId,
  documents,
}: DocumentLinesProps) => {
  const { reorderKey } = useContext(ChartReorderContext);
  const [positions, setPositions] = useState<PositionWithId[]>([]);
  const { parentDocumentIds } = document;

  useEffect(() => {
    const measurePositions = () => {
      const newPositions: PositionWithId[] = [];
      const refsMap = documentRefsMap.current;

      parentDocumentIds.forEach((parentId) => {
        const childRef = refsMap?.get(document.id);
        const parentRef = refsMap?.get(parentId);

        if (childRef && parentRef && containerRef?.current) {
          const parent = parentRef.getBoundingClientRect();
          const child = childRef.getBoundingClientRect();
          const container = containerRef.current.getBoundingClientRect();

          newPositions.push({
            parentX: parent.left - container.left,
            parentY: parent.bottom - container.top,
            childX: child.left - container.left,
            childY: child.top - container.top,
            toId: parentId,
          });
        }
      });

      setPositions(newPositions);
    };

    // After a reorder, dnd-kit's drop animation takes ~250ms while the
    // dragged card remains in the browser's popover top layer. The
    // Feedback plugin cleanup (which restores the card to normal document
    // flow) only runs after the animation finishes. Delay measurement so
    // we read final flow positions instead of popover-layer positions.
    if (reorderKey) {
      const timer = setTimeout(measurePositions, 300);
      return () => clearTimeout(timer);
    }

    measurePositions();
  }, [
    containerRef,
    parentDocumentIds,
    document.id,
    documentRefsMap,
    documents,
    reorderKey,
  ]);

  const isHovering = hoveredId === document.id;

  return (
    <>
      {positions.map(({ toId, ...position }) => (
        <Line
          key={toId}
          {...position}
          offsetX={75}
          active={isHovering || hoveredId === toId}
        />
      ))}
    </>
  );
};

export const DocumentCard = ({
  document,
  documentRefsMap,
  hoveredId,
  setHoveredId,
  setSelectedId,
  isDragging,
  handleRef,
}: Props) => {
  const { t } = useTranslation();

  const { parentDocumentIds, childDocumentIds, linkedDocumentIds } = document;
  const release = document.latestPublishedRelease ?? document.latestRelease;

  const { documentsById, tierOrderById } = useContext(
    ChartLinkedDocumentsContext,
  );

  const tierOrderOf = (doc: DocumentSummary) =>
    doc.tier ? tierOrderById.get(doc.tier.id) : undefined;
  const myTierOrder = tierOrderOf(document);

  const resolveDocuments = (ids: number[]) =>
    ids
      .map((id) => documentsById.get(id))
      .filter((doc): doc is DocumentSummary => Boolean(doc));

  const linedIds = new Set([...parentDocumentIds, ...childDocumentIds]);
  const sitsOnTop = (doc: DocumentSummary) => {
    const order = tierOrderOf(doc);
    return (
      order !== undefined && myTierOrder !== undefined && order <= myTierOrder
    );
  };

  const overflow = resolveDocuments(linkedDocumentIds).filter(
    (doc) => !linedIds.has(doc.id),
  );
  const topOverflow = overflow.filter(sitsOnTop);
  const bottomOverflow = overflow.filter((doc) => !sitsOnTop(doc));

  // Only show a dot where the card actually links somewhere: a connecting line
  // for adjacent tiers, else an overflow "+count".
  const hasTopLinks = parentDocumentIds.length > 0 || topOverflow.length > 0;
  const hasBottomLinks =
    childDocumentIds.length > 0 || bottomOverflow.length > 0;

  const isHovering = hoveredId === document.id;
  const isHoveringAbove =
    parentDocumentIds.some((id) => hoveredId === id) ||
    topOverflow.some((doc) => hoveredId === doc.id);
  const isHoveringBelow =
    childDocumentIds.some((id) => hoveredId === id) ||
    bottomOverflow.some((doc) => hoveredId === doc.id);

  const isDraft = document.latestPublishedRelease === null;

  const dateAttribute = isPastDate(release.adoptedAt)
    ? "adoptedAt"
    : "publishedAt";

  const formattedDate = formatDate(
    dateAttribute === "adoptedAt"
      ? release.adoptedAt
      : release.artifact.publishedAt,
  );

  const dateLabel =
    dateAttribute === "adoptedAt"
      ? t(($) => $.commons.document.adopted)
      : t(($) => $.commons.document.statuses.published);

  return (
    <TooltipProvider>
      <div
        ref={(node) => {
          if (node) {
            documentRefsMap.current?.set(document.id, node);
          } else {
            documentRefsMap.current?.delete(document.id);
          }
        }}
        onMouseEnter={() => setHoveredId(document.id)}
        onMouseLeave={() => setHoveredId(undefined)}
        className={cn(
          "relative size-37.5 shrink-0",
          isDragging &&
            "border-success/50 scale-[0.97] rounded-sm border-2 border-dashed opacity-50",
        )}
      >
        {hasTopLinks && (
          <LinkedDocumentsDot
            position="top"
            active={isHovering || isHoveringAbove}
            documents={topOverflow}
          />
        )}

        {handleRef && (
          <span
            ref={handleRef}
            className="text-muted-foreground/60 hover:text-muted-foreground absolute top-2 left-2 z-10 cursor-grab"
            aria-hidden="true"
          >
            <GripVertical className="size-4" />
          </span>
        )}

        <div className="absolute top-2 right-2 z-10">
          <InfoIconButton onClick={() => setSelectedId(document.id)} />
        </div>
        <Link
          to={`/documents/${document.id}?releaseId=${release.id}&tab=metadata`}
          title={document.latestRelease.artifact.title}
          className={cn(
            "bg-card hover:border-chart-1 relative flex h-full cursor-pointer flex-col overflow-hidden rounded-sm border-2 border-transparent p-2 shadow-md",
            isDragging && "pointer-events-none",
            isDraft && "border-warning/50 hover:border-solid",
          )}
        >
          {isDraft && (
            <>
              <div
                className="bg-warning/50 pointer-events-none absolute right-0 bottom-0 size-15"
                style={{ clipPath: "polygon(100% 35%, 100% 100%, 0 100%)" }}
              />
            </>
          )}
          <div className="size-12 p-2.5">
            <DocumentIcon type={document.latestRelease.artifact.fileType} />
          </div>
          <div className="text-secondary-foreground line-clamp-4 flex-1 overflow-hidden text-xs font-medium wrap-break-word hyphens-auto">
            {document.latestRelease.artifact.title}
          </div>
          {!isDraft && (
            <div className="text-muted-foreground text-[11px]">
              {`${dateLabel} ${formattedDate}`}
            </div>
          )}
          {isDraft && (
            <StatusBadge.DocumentArtifactStatus
              status={document.latestRelease.artifact.status}
            />
          )}
        </Link>
        {hasBottomLinks && (
          <LinkedDocumentsDot
            position="bottom"
            active={isHovering || isHoveringBelow}
            documents={bottomOverflow}
          />
        )}
      </div>
    </TooltipProvider>
  );
};
